/**
 * GettingStartedChecklist
 * =======================
 *
 * A floating, funnel-aware setup helper. It derives each step's state from
 * data the dashboard already has (workspace → sources/signals → goals →
 * serving) and always points at the one correct next action.
 *
 * Unlike the old inline banner it never pushes the page layout around: it
 * floats bottom-right, can be minimized to a compact pill, and the X
 * dismisses it permanently (persisted globally in localStorage — once closed
 * it never comes back, in any workspace). It also disappears on its own once
 * the workspace is fully set up.
 */

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, IconButton, Stack, LinearProgress,
  Link, ButtonBase, alpha, useTheme,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CloseIcon from '@mui/icons-material/Close';
import RemoveIcon from '@mui/icons-material/Remove';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useWorkspace } from '../../contexts/WorkspaceContext';

// Global "never show again" flag — closing the helper is forever, across
// workspaces. The legacy per-workspace keys are still honored (and migrated)
// so users who dismissed the old banner don't see it resurrected.
const DISMISS_KEY = 'GETTING_STARTED_DISMISSED';
const LEGACY_DISMISS_PREFIX = 'GETTING_STARTED_DISMISSED_';
const COLLAPSE_KEY = 'GETTING_STARTED_COLLAPSED';

const isServing = (s) => s === 'serving' || s === 'live';

// "First live forecast" = anything that proves the pipeline delivered:
// a serving goal, a crowned champion model (autopilot finished fitting and
// promoting), a scored champion, or an active deployment. The goal status
// alone is not enough — a goal can settle at "idle" when its run completes
// while the deployment is still coming up, even though forecasts exist.
const hasLiveForecast = (goalsList, summary) => (
  goalsList.some((g) => isServing(g.status) || g.champion_pipeline_id
    || g.accuracy != null || g.skill != null)
  || (summary?.deployments?.active || 0) > 0
);

const GettingStartedChecklist = ({ summary, goals }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { activeWorkspaceId } = useWorkspace();

  const [dismissed, setDismissed] = useState(() => {
    try {
      if (localStorage.getItem(DISMISS_KEY) === '1') return true;
      // Migrate a legacy per-workspace dismissal to the global flag.
      if (localStorage.getItem(`${LEGACY_DISMISS_PREFIX}${activeWorkspaceId || 'default'}`) === '1') {
        localStorage.setItem(DISMISS_KEY, '1');
        return true;
      }
      return false;
    } catch (_) { return false; }
  });
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch (_) { return false; }
  });

  const goalsList = Array.isArray(goals) ? goals : [];
  const hasData = (summary?.sources?.total || 0) > 0 || (summary?.data?.total_signals || 0) > 0;
  const hasGoal = goalsList.length > 0;
  const forecastLive = hasLiveForecast(goalsList, summary);

  const steps = useMemo(() => ([
    {
      id: 'workspace',
      label: 'Create your workspace',
      done: true, // rendering the dashboard implies an active workspace
    },
    {
      id: 'data',
      label: 'Connect your data',
      done: hasData,
      cta: { label: 'Connect data', to: '/datasources/connect' },
      alt: { label: 'or set up in chat', to: '/analyst' },
    },
    {
      id: 'goal',
      label: 'Create your first goal',
      done: hasGoal,
      cta: { label: 'Create a goal', to: '/goals/create' },
      hint: hasData ? null : 'Connect data first — a goal needs a signal to target.',
    },
    {
      id: 'forecast',
      label: 'Watch your first live forecast',
      done: forecastLive,
      cta: hasGoal
        ? { label: 'Open your goal', to: `/goals/${goalsList[0].goal_id}` }
        : null,
      hint: hasGoal
        ? 'Your goal is being built — drivers, models and deployment run automatically.'
        : null,
    },
  ]), [hasData, hasGoal, forecastLive, goalsList]);

  const doneCount = steps.filter((s) => s.done).length;
  const allDone = doneCount === steps.length;
  // The single "current" step: the first incomplete one. Its CTA is the only
  // button — every other incomplete step waits its turn.
  const current = steps.find((s) => !s.done);

  // Wait for real data before judging progress, and stay hidden once
  // dismissed or complete.
  if (!summary || dismissed || allDone) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch (_) { /* ignore */ }
    setDismissed(true);
  };

  const setCollapse = (value) => {
    try { localStorage.setItem(COLLAPSE_KEY, value ? '1' : '0'); } catch (_) { /* ignore */ }
    setCollapsed(value);
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: { xs: 16, sm: 24 },
        right: { xs: 16, sm: 24 },
        zIndex: theme.zIndex.modal - 1,
      }}
    >
      {collapsed ? (
        <ButtonBase
          onClick={() => setCollapse(false)}
          aria-label="Open getting started checklist"
          sx={{
            borderRadius: 99,
            bgcolor: 'background.paper',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
            boxShadow: theme.shadows[4],
            px: 1.75,
            py: 0.9,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) },
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 17, color: 'primary.main' }} />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Getting started
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            {doneCount}/{steps.length}
          </Typography>
        </ButtonBase>
      ) : (
        <Paper
          elevation={6}
          sx={{
            width: 320,
            maxWidth: 'calc(100vw - 32px)',
            borderRadius: 2,
            overflow: 'hidden',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ px: 2, pt: 1.5, pb: 1 }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
              Getting started
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {doneCount} of {steps.length} done
            </Typography>
            <IconButton
              size="small"
              onClick={() => setCollapse(true)}
              aria-label="Minimize getting started checklist"
            >
              <RemoveIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={dismiss}
              aria-label="Dismiss getting started checklist permanently"
              title="Close — this won't show again"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>

          <LinearProgress
            variant="determinate"
            value={(doneCount / steps.length) * 100}
            sx={{ height: 3 }}
          />

          <Box sx={{ px: 2, py: 1.5 }}>
            <Stack spacing={0.9}>
              {steps.map((s) => {
                const isCurrent = current && s.id === current.id;
                return (
                  <Stack key={s.id} direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                    {s.done
                      ? <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                      : (
                        <RadioButtonUncheckedIcon
                          sx={{ fontSize: 16, color: isCurrent ? 'primary.main' : 'text.disabled' }}
                        />
                      )}
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{
                        fontWeight: isCurrent ? 700 : 500,
                        color: s.done ? 'text.secondary' : isCurrent ? 'text.primary' : 'text.disabled',
                        textDecoration: s.done ? 'line-through' : 'none',
                        textDecorationColor: alpha(theme.palette.text.secondary, 0.5),
                      }}
                    >
                      {s.label}
                    </Typography>
                  </Stack>
                );
              })}
            </Stack>

            {current?.hint && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {current.hint}
              </Typography>
            )}

            {current?.cta && (
              <Stack spacing={0.5} sx={{ mt: 1.5 }} alignItems="stretch">
                <Button
                  variant="contained"
                  size="small"
                  fullWidth
                  onClick={() => navigate(current.cta.to)}
                >
                  {current.cta.label}
                </Button>
                {current.alt && (
                  <Link
                    component="button"
                    type="button"
                    variant="caption"
                    underline="hover"
                    color="text.secondary"
                    onClick={() => navigate(current.alt.to)}
                    sx={{ alignSelf: 'center' }}
                  >
                    {current.alt.label}
                  </Link>
                )}
              </Stack>
            )}
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default GettingStartedChecklist;
